import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Send } from "lucide-react";
import { api } from "../../lib/api";
import { PickupPointsManager } from "./PickupPointsManager";
import { StockManager } from "./StockManager";
import { DeliverEbookDialog } from "./DeliverEbookDialog";
import { PendingDeliveries } from "./PendingDeliveries";

const opts = { withCredentials: true };
export const ORDER_STATUSES = [
  { v: "demande", l: "Demande WhatsApp", c: "bg-[#E8E4D9] text-[#1A1A1A]" },
  { v: "payee", l: "Payée", c: "bg-[#2C402E] text-[#FAF9F6]" },
  { v: "livree", l: "Retirée / livrée", c: "bg-[#1A1A1A] text-[#FAF9F6]" },
  { v: "annulee", l: "Annulée", c: "bg-[#9E4735]/15 text-[#9E4735]" },
];
const VERSIONS = { papier: "Papier", numerique: "Numérique", livre: "Non précisée" };
const EMPTY = { status: "demande", version: "papier", slug: "", customer_name: "", customer_phone: "", city: "", pickup: "", amount: "", note: "" };
const inputCls = "w-full bg-[#FAF9F6] border border-[#D1CFC7] focus:border-[#2C402E] px-3 py-2 outline-none font-body text-sm";
const fcfa = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} F CFA`;

export function OrdersPanel() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [extracts, setExtracts] = useState([]);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState(null);
  const [q, setQ] = useState("");
  const [deliver, setDeliver] = useState(null);
  const load = () => {
    api.get("/admin/orders", { ...opts, params: filter ? { status: filter } : {} }).then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/admin/orders/summary", opts).then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/admin/extracts", opts).then((r) => setExtracts(r.data)).catch(() => {}); }, []);

  const save = async (data) => {
    const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data[k] === "" || data[k] === undefined ? null : data[k]]));
    payload.status = data.status; payload.version = data.version;
    if (payload.amount !== null) payload.amount = Number(payload.amount);
    try {
      if (data.id) await api.put(`/admin/orders/${data.id}`, payload, opts);
      else await api.post("/admin/orders", payload, opts);
      toast.success("Commande enregistrée");
      setForm(null); load();
    } catch { toast.error("Vérifiez les champs (montant numérique)"); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Supprimer la commande ${o.ref} ?`)) return;
    try { await api.delete(`/admin/orders/${o.id}`, opts); load(); } catch { toast.error("Suppression impossible"); }
  };

  return (
    <div data-testid="orders-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="font-serif text-4xl text-[#1A1A1A] font-light">Commandes</h2>
        <button onClick={() => setForm({ ...EMPTY })} data-testid="new-order-button" className="bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body flex items-center gap-2"><Plus size={14} /> Saisir une commande</button>
      </div>
      <p className="text-sm font-body text-[#757575] mb-6 max-w-3xl">Chaque clic sur un bouton d'achat crée une « Demande WhatsApp » avec une référence (ex. CLED-4F2A7) reprise dans le message du lecteur. Retrouvez-la ici et faites évoluer son statut.</p>
      <PendingDeliveries key={JSON.stringify(items.map((o) => [o.id, o.status]))} onDone={load} />
      {summary && <Summary summary={summary} />}
      {form && <OrderForm form={form} setForm={setForm} extracts={extracts} onSave={() => save(form)} onCancel={() => setForm(null)} />}
      <div className="flex flex-wrap gap-2 my-6">
        {[{ v: "", l: "Toutes" }, ...ORDER_STATUSES].map((s) => (
          <button key={s.v} onClick={() => setFilter(s.v)} data-testid={`orders-filter-${s.v || "all"}`} className={`px-3 py-1.5 text-xs font-body border transition-colors ${filter === s.v ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]" : "border-[#D1CFC7] text-[#4A4A4A]"}`}>{s.l}</button>
        ))}
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une référence, un client, un livre…" data-testid="orders-search-input" className={`${inputCls} max-w-md mb-4`} />
      <OrdersTable items={items.filter((o) => !q || [o.ref, o.customer_name, o.customer_phone, o.title].some((v) => v && v.toLowerCase().includes(q.toLowerCase())))} onStatus={(o, status) => save({ ...EMPTY, ...o, slug: "", status })} onEdit={(o) => setForm({ ...EMPTY, ...o, slug: o.slug || "", amount: o.amount ?? "" })} onDelete={remove} onDeliver={setDeliver} />
      <DeliverEbookDialog order={deliver} extracts={extracts} open={!!deliver} onOpenChange={(o) => !o && setDeliver(null)} onDone={load} />
      <div className="mt-12"><PickupPointsManager /></div>
      <div className="mt-12"><StockManager key={JSON.stringify(items.map((o) => [o.id, o.status, o.pickup]))} /></div>
    </div>
  );
}

const Summary = ({ summary }) => (
  <div data-testid="orders-summary" className="grid grid-cols-2 lg:grid-cols-5 gap-3">
    {ORDER_STATUSES.map((s) => (
      <div key={s.v} className="border border-[#D1CFC7] p-4"><p className="text-[10px] uppercase tracking-[0.2em] text-[#757575] font-body">{s.l}</p><p className="font-serif text-3xl" data-testid={`orders-count-${s.v}`}>{summary.by_status[s.v]}</p></div>
    ))}
    <div className="border border-[#2C402E] p-4"><p className="text-[10px] uppercase tracking-[0.2em] text-[#757575] font-body">Chiffre d'affaires</p><p className="font-serif text-2xl" data-testid="orders-revenue">{fcfa(summary.revenue)}</p></div>
    {[["Par version", summary.by_version, (k) => VERSIONS[k] || k], ["Par partenaire", summary.by_partner, (k) => (k === "—" ? "Sans partenaire" : k)], ["Par livre", summary.by_title, (k) => k]].map(([t, rows, lbl]) => (
      <div key={t} className="col-span-2 lg:col-span-5 xl:col-span-1 border border-[#E8E4D9] p-4 text-xs font-body">
        <p className="uppercase tracking-[0.2em] text-[#9E4735] mb-2">{t}</p>
        {rows.length === 0 ? <p className="text-[#757575]">—</p> : rows.slice(0, 6).map((r) => <p key={r.key} className="flex justify-between gap-2"><span className="truncate">{lbl(r.key)}</span><span>{r.total} ({r.paid} payée{r.paid > 1 ? "s" : ""}{r.revenue ? ` · ${fcfa(r.revenue)}` : ""})</span></p>)}
      </div>
    ))}
  </div>
);

const OrdersTable = ({ items, onStatus, onEdit, onDelete, onDeliver }) => (
  <div className="overflow-x-auto border border-[#D1CFC7]">
    <table className="w-full text-xs font-body whitespace-nowrap">
      <thead className="bg-[#F4F1EA] text-left uppercase tracking-wider text-[#4A4A4A]">
        <tr>{["Réf.", "Date", "Livre", "Version", "Retrait", "Partenaire", "Client", "Montant", "Statut", ""].map((h) => <th key={h} className="p-3">{h}</th>)}</tr>
      </thead>
      <tbody>
        {items.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-[#757575]" data-testid="orders-empty">Aucune commande pour l'instant.</td></tr>}
        {items.map((o) => (
          <tr key={o.id} data-testid={`order-row-${o.ref}`} className="border-t border-[#E8E4D9]">
            <td className="p-3 font-mono text-[#1A1A1A]">{o.ref}</td>
            <td className="p-3">{new Date(o.created_at).toLocaleDateString("fr-FR")}</td>
            <td className="p-3 max-w-[180px] truncate">{o.title || "—"}</td>
            <td className="p-3">{VERSIONS[o.version]}{o.price_label ? <span className="block text-[#757575]">{o.price_label}</span> : null}{o.note?.includes("Offre de lancement") ? <span data-testid={`order-offer-badge-${o.ref}`} className="inline-block mt-1 px-1.5 py-0.5 text-[10px] bg-[#9E4735] text-[#FAF9F6]">Offre -{o.note.match(/-(\d+) %/)?.[1]} %</span> : null}{o.source === "cinetpay" ? <span className="inline-block mt-1 ml-1 px-1.5 py-0.5 text-[10px] bg-[#2C402E] text-[#FAF9F6]">En ligne</span> : null}</td>
            <td className="p-3">{o.city ? `${o.city}${o.pickup ? ` – ${o.pickup}` : ""}` : "—"}</td>
            <td className="p-3">{o.lieu_name || "—"}</td>
            <td className="p-3">{o.customer_name || "—"}{o.customer_phone ? <span className="block text-[#757575]">{o.customer_phone}</span> : null}</td>
            <td className="p-3">{o.amount ? fcfa(o.amount) : "—"}</td>
            <td className="p-3">
              <select value={o.status} onChange={(e) => onStatus(o, e.target.value)} data-testid={`order-status-${o.ref}`} className={`px-2 py-1 border-0 text-xs ${ORDER_STATUSES.find((s) => s.v === o.status)?.c}`}>
                {ORDER_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </td>
            <td className="p-3 text-right">
              {o.version === "numerique" && !["annulee", "livree"].includes(o.status) && <button onClick={() => onDeliver(o)} data-testid={`order-deliver-${o.ref}`} title="Livrer le livre numérique" className="text-[#9E4735] mr-3 inline-flex items-center gap-1"><Send size={13} /> Livrer</button>}
              <button onClick={() => onEdit(o)} data-testid={`order-edit-${o.ref}`} className="text-[#2C402E] mr-3"><Pencil size={13} /></button>
              <button onClick={() => onDelete(o)} data-testid={`order-delete-${o.ref}`} className="text-[#9E4735]"><Trash2 size={13} /></button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const OrderForm = ({ form, setForm, extracts, onSave, onCancel }) => {
  const set = (k, v) => setForm({ ...form, [k]: v });
  const field = (k, label, props = {}) => (
    <div><label className="text-xs font-body text-[#4A4A4A] mb-1 block">{label}</label><input value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`order-${k}-input`} className={inputCls} {...props} /></div>
  );
  return (
    <div data-testid="order-form" className="border border-[#2C402E] bg-[#F4F1EA] p-6 mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
      <h3 className="md:col-span-3 font-serif text-2xl">{form.id ? `Commande ${form.ref}` : "Nouvelle commande"}</h3>
      <div><label className="text-xs font-body text-[#4A4A4A] mb-1 block">Livre</label>
        <select value={form.slug || ""} onChange={(e) => set("slug", e.target.value)} data-testid="order-slug-input" className={inputCls}>
          <option value="">{form.title || "— Choisir —"}</option>
          {extracts.map((e) => <option key={e.id} value={e.slug}>{e.title}</option>)}
        </select>
      </div>
      <div><label className="text-xs font-body text-[#4A4A4A] mb-1 block">Version</label>
        <select value={form.version} onChange={(e) => set("version", e.target.value)} data-testid="order-version-input" className={inputCls}>
          {Object.entries(VERSIONS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div><label className="text-xs font-body text-[#4A4A4A] mb-1 block">Statut</label>
        <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="order-status-input" className={inputCls}>
          {ORDER_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
      </div>
      {field("customer_name", "Nom du client")}
      {field("customer_phone", "Téléphone / WhatsApp")}
      {field("amount", "Montant encaissé (F CFA)", { type: "number", min: 0 })}
      {field("city", "Ville")}
      {field("pickup", "Librairie de retrait")}
      {field("note", "Note")}
      <div className="md:col-span-3 flex gap-2">
        <button onClick={onSave} data-testid="save-order" className="bg-[#2C402E] text-[#FAF9F6] px-6 py-2 text-sm font-body">Enregistrer</button>
        <button onClick={onCancel} data-testid="cancel-order" className="border border-[#D1CFC7] px-6 py-2 text-sm font-body">Annuler</button>
      </div>
    </div>
  );
};
